export type ChartContextUnit = "gbp" | "hours" | "percent" | "days" | "count";

export interface ChartContextSeries {
  name: string;
  values: (number | null)[];
}

export interface ChartContextData {
  labels: string[];
  series: ChartContextSeries[];
  unit?: ChartContextUnit;
}

type Row = Record<string, unknown>;

const asNumber = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

const asLabel = (v: unknown): string => (v == null ? "" : String(v));

export function contextFromRows(
  rows: Row[] | undefined | null,
  labelKey: string,
  valueKeys: Record<string, string>,
  unit?: ChartContextUnit,
): ChartContextData | null {
  if (!rows?.length) return null;
  return {
    labels: rows.map(r => asLabel(r[labelKey])),
    series: Object.entries(valueKeys).map(([key, name]) => ({
      name,
      values: rows.map(r => asNumber(r[key])),
    })),
    unit,
  };
}

export function contextFromWideRows(
  rows: Row[] | undefined | null,
  labelKey: string,
  unit?: ChartContextUnit,
): ChartContextData | null {
  if (!rows?.length) return null;
  const seriesKeys = Object.keys(rows[0]).filter(k => k !== labelKey);
  return {
    labels: rows.map(r => asLabel(r[labelKey])),
    series: seriesKeys.map(k => ({ name: k, values: rows.map(r => asNumber(r[k])) })),
    unit,
  };
}

export function contextFromGrouped(
  grouped: { data: Row[]; series: string[] } | undefined | null,
  labelKey: string,
  unit?: ChartContextUnit,
): ChartContextData | null {
  if (!grouped?.data?.length) return null;
  return {
    labels: grouped.data.map(r => asLabel(r[labelKey])),
    series: (grouped.series ?? []).map(name => ({
      name,
      values: grouped.data.map(r => asNumber(r[name])),
    })),
    unit,
  };
}


export function contextFromDatasets(
  source:
    | { labels?: string[]; datasets?: { label?: string; data?: (number | null)[] }[] }
    | undefined
    | null,
  unit?: ChartContextUnit,
): ChartContextData | null {
  if (!source?.labels?.length) return null;
  return {
    labels: source.labels.map(asLabel),
    series: (source.datasets ?? []).map((d, i) => ({
      name: d.label ?? `Series ${i + 1}`,
      values: (d.data ?? []).map(asNumber),
    })),
    unit,
  };
}

export interface ChartContextSource {
  id: string;
  title: string;
  description: string;
  getContextData: () => ChartContextData | null;
}
